import { useState } from 'react';
import {
    Typography,
    Box,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    CircularProgress,
    Alert,
    Link,
    InputAdornment,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import PaymentIcon from '@mui/icons-material/Payment';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PageHeader } from '../components/PageHeader';
import { CenterSelect } from '../components/CenterSelect';
import { paymentsApi, CaInvoiceStatus, InvoiceSearchType } from '../services/payments-api.service';

export function PaymentsPage() {
    const [searchType, setSearchType] = useState<InvoiceSearchType>('center');
    const [searchValue, setSearchValue] = useState('');
    const [invoices, setInvoices] = useState<CaInvoiceStatus[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searched, setSearched] = useState(false);

    const handleSearch = async () => {
        if (!searchValue.trim()) {
            setError(`Please ${searchType === 'email' ? 'enter an email address' : 'select a centre'}`);
            return;
        }

        setLoading(true);
        setError(null);
        setSearched(true);

        try {
            const result = await paymentsApi.searchInvoices(searchType, searchValue.trim());
            if (result.errors && result.errors.length > 0) {
                setError(result.errors.join(', '));
                setInvoices([]);
            } else {
                setInvoices(result.invoices);
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to fetch invoices');
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearchTypeChange = (_: React.MouseEvent<HTMLElement>, newType: InvoiceSearchType | null) => {
        if (newType !== null) {
            setSearchType(newType);
            setSearchValue('');
            setSearched(false);
            setInvoices([]);
            setError(null);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
        switch (status) {
            case 'paid':
                return 'success';
            case 'open':
                return 'warning';
            case 'void':
            case 'uncollectible':
                return 'error';
            default:
                return 'default';
        }
    };

    const formatCurrency = (amount: number, currency: string) => {
        return new Intl.NumberFormat('en-CA', {
            style: 'currency',
            currency: currency.toUpperCase(),
        }).format(amount / 100);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('en-CA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <PageHeader currentPath="/payments">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PaymentIcon sx={{ fontSize: 28, mt: 0.3, mr: 1.5, color: 'primary.main' }} />
                <Typography variant="h5" component="h1" fontWeight={600}>
                    Payments
                </Typography>
            </Box>

            {/* Search Section */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                <ToggleButtonGroup
                    color="primary"
                    value={searchType}
                    exclusive
                    onChange={handleSearchTypeChange}
                    size="small"
                >
                    <ToggleButton value="center">Centre</ToggleButton>
                    <ToggleButton value="email">Email</ToggleButton>
                </ToggleButtonGroup>
                {searchType === 'email' ? (
                    <TextField
                        label="Email"
                        variant="outlined"
                        size="small"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        sx={{ width: 350 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />
                ) : (
                    <Box sx={{ width: 350 }}>
                        <CenterSelect value={searchValue} onChange={(value) => setSearchValue(value)} label="Centre" />
                    </Box>
                )}
                <Button variant="contained" onClick={handleSearch} disabled={loading} sx={{ minWidth: 100 }}>
                    {loading ? <CircularProgress size={24} /> : 'Search'}
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Results Section */}
            {searched && !loading && !error && (
                <>
                    {invoices.length === 0 ? (
                        <Alert severity="info">No invoices found for {searchValue}</Alert>
                    ) : (
                        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 280px)' }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Invoice #</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Teacher</TableCell>
                                        <TableCell>Course Date</TableCell>
                                        <TableCell>Items</TableCell>
                                        <TableCell align="right">Amount</TableCell>
                                        <TableCell>Due Date</TableCell>
                                        <TableCell>Paid At</TableCell>
                                        <TableCell>Payment Link</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoices.map((invoice) => (
                                        <TableRow key={invoice.invoiceId} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace">
                                                    {invoice.invoiceNumber}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={invoice.status}
                                                    color={getStatusColor(invoice.status)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{invoice.userName || '-'}</TableCell>
                                            <TableCell>{invoice.userEmail || '-'}</TableCell>
                                            <TableCell>{invoice.teacherName || '-'}</TableCell>
                                            <TableCell>{formatDate(invoice.courseDate)}</TableCell>
                                            <TableCell sx={{ minWidth: 200, maxWidth: 300 }}>
                                                <Typography variant="body2">
                                                    {invoice.itemDescriptions.join(' | ')}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {formatCurrency(invoice.amountDue, invoice.currency)}
                                            </TableCell>
                                            <TableCell>{formatDate(invoice.dueDate)}</TableCell>
                                            <TableCell>{formatDate(invoice.paidAt)}</TableCell>
                                            <TableCell>
                                                {invoice.paymentUrl ? (
                                                    <Link
                                                        href={invoice.paymentUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                        }}
                                                    >
                                                        <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                    </Link>
                                                ) : (
                                                    '-'
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </>
            )}
        </PageHeader>
    );
}
